/**
 * @license
 * Copyright 厦门乾元盛世科技有限公司 All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file.
 */



import { Component, Input, Output, EventEmitter, ElementRef, Renderer, OnInit, ViewEncapsulation } from '@angular/core';

import { StringUtils } from '../util/string.utils';
import KeyCode from '../util/key.code';
/**
 * @param {?} value
 * @return {?}
 */
function isInteger(value: number): boolean {
    return typeof value === 'number' &&
        isFinite(value) &&
        Math.floor(value) === value;
}

const /** @type {?} */ EDIT_KEYS = [KeyCode.LEFT, KeyCode.RIGHT, KeyCode.UP, KeyCode.DOWN, KeyCode.DELETE, KeyCode.BACKSPACE];
export class Pagination implements OnInit {
/**
 * 样式前缀
 */
prefixCls = 'ant-pagination';
/**
 * 数据总数
 */
total = 0;
/**
 * 当前页数，默认1
 */
current = 1;
/**
 * 每页条数，默认10
 */
pageSize = 10;
/**
 * 是否可以改变 pageSize。默认为false
 */
showSizeChanger = false;
/**
 * 指定每页可以显示多少条
 */
pageSizeOptions: number[] = [10, 20, 30, 40];
/**
 * 每页数选择器中的下拉选项格式，默认形如：`10条/页`
 */
pageSizeOptionFormat = '{pageSize}{perPage}';
/**
 * 是否可以快速跳转至某页。默认为false
 */
showQuickJumper = false;
/**
 * 当为「small」时，是小尺寸分页
 */
size: string;
/**
 * 当添加该属性时，显示为简单分页
 */
simple = false;
/**
 * 向前后一次跳多少页：showLess为3，否则为5
 */
showLessItems = false;
/**
 * 是否在按钮上显示title
 */
showTitle = true;
/**
 * 是否显示记录总数和当前记录范围
 */
showTotal = false;
/**
 * 自定义显示总数的方法，参数：total-总数，start-开始记录数，end-结束记录数
 */
showTotalFunc: (total: number, start: number, end: number) => string;
/**
 * 用户CSS样式
 */
class: string;
/**
 * 本地化
 */
locale = {
        // Options
        items_per_page: '条/页',
        jump_to: '跳至',
        page: '页',

        // Pagination
        prev_page: '上一页',
        next_page: '下一页',
        prev_5: '向前 5 页',
        next_5: '向后 5 页',
        prev_3: '向前 3 页',
        next_3: '向后 3 页',
    };
/**
 * pageSize变更事件
 */
sizeChange = new EventEmitter<{ current: number, size: number }>();
/**
 * page变更事件
 */
pageChange = new EventEmitter<{ page: number, pageSize: number }>();
public _current: number;
public _pageSize: number;
/**
 * @param {?} renderer
 * @param {?} elementRef
 */
constructor(
private renderer: Renderer,
private elementRef: ElementRef) {

    }
/**
 * @return {?}
 */
ngOnInit(): void {
        this._current = this.current;
        this._pageSize = this.pageSize;
    }
/**
 * 根据「总数」和「每页条数」计算「总页数」
 * @return {?}
 */
private getPageCount(): number {
        return Math.floor((this.total - 1) / this.pageSize) + 1;
    }
/**
 * @param {?} page
 * @return {?}
 */
private isValid(page: number): boolean {
        return isInteger(page) && page >= 1 && page !== this.current;
    }
/**
 * @return {?}
 */
public hasPrev(): boolean {
        return this.current > 1;
    }
/**
 * @return {?}
 */
public hasNext(): boolean {
        return this.current < this.getPageCount();
    }
/**
 * 前一页
 * @return {?}
 */
public prev(): void {
        if (this.hasPrev()) {
            this.handleChange(this.current - 1);
        }
    }
/**
 * 后一页
 * @return {?}
 */
public next(): void {
        if (this.hasNext()) {
            this.handleChange(this.current + 1);
        }
    }
/**
 * 第一页
 * @return {?}
 */
public first(): void {
        this.handleChange(1);
    }
/**
 * 末一页
 * @return {?}
 */
public last(): void {
        this.handleChange(this.getPageCount());
    }
/**
 * 向前跳3页或5页
 * @return {?}
 */
public jumpPrev(): void {
        this.handleChange(Math.max(1, this.current - (this.showLessItems ? 3 : 5)));
    }
/**
 * 向后跳3页或5页
 * @return {?}
 */
public jumpNext(): void {
        const /** @type {?} */ pageCount: number = this.getPageCount();
        this.handleChange(Math.min(pageCount, this.current + (this.showLessItems ? 3 : 5)));
    }
/**
 * @return {?}
 */
public getJumpPrevTitle(): string {
        return this.showLessItems ? this.locale.prev_3 : this.locale.prev_5;
    }
/**
 * @return {?}
 */
public getJumpNextTitle(): string {
        return this.showLessItems ? this.locale.next_3 : this.locale.next_5;
    }
/**
 * @return {?}
 */
public isJumpPrevVisible(): boolean {
        const /** @type {?} */ pageCount = this.getPageCount();
        const /** @type {?} */ pageBufferSize = this.showLessItems ? 1 : 2;
        if (pageCount <= 5 + pageBufferSize * 2) {
            return false;
        }
        return (this.current - 1 >= pageBufferSize * 2) && (this.current !== 1 + 2);
    }
/**
 * @return {?}
 */
public isJumpNextVisible(): boolean {
        const /** @type {?} */ pageCount = this.getPageCount();
        const /** @type {?} */ pageBufferSize = this.showLessItems ? 1 : 2;
        if (pageCount <= 5 + pageBufferSize * 2) {
            return false;
        }
        return (pageCount - this.current >= pageBufferSize * 2) && (this.current !== pageCount - 2);
    }
/**
 * @return {?}
 */
public isFirstPagerVisible(): boolean {
        const /** @type {?} */ range: number[] = this.getPagerRange();
        return !range.includes(1);
    }
/**
 * @return {?}
 */
public isLastPagerVisible(): boolean {
        const /** @type {?} */ pageCount = this.getPageCount();
        const /** @type {?} */ range: number[] = this.getPagerRange();
        return !range.includes(pageCount);
    }
/**
 * 导航至不同的页码
 * @param {?} value
 * @return {?}
 */
public handleChange(value: any): void {
        let /** @type {?} */ page = Number(value);
        if (this.isValid(page)) {
            const /** @type {?} */ pageCount: number = this.getPageCount();

            if (page > pageCount) {
                page = pageCount;
            }
            this.current = this._current = page;
            const /** @type {?} */ pageSize = this.pageSize;
            this.pageChange.emit({ page: page, pageSize: pageSize });
        }
    }
/**
 * 更改「每页条数」
 * @return {?}
 */
public handlePageSizeChange(): void {
        const /** @type {?} */ newPageSize: number = Number(this._pageSize);
        if (this.pageSize !== newPageSize) {
            this.pageSize = this._pageSize = newPageSize;
            this.pageChange.emit({ page: this.current, pageSize: newPageSize });
        }
    }
/**
 * 获取页码显示的范围
 * @return {?}
 */
getPagerRange(): number[] {
        const /** @type {?} */ pageCount = this.getPageCount();
        const /** @type {?} */ pageBufferSize = this.showLessItems ? 1 : 2;

        let /** @type {?} */ left = Math.max(1, this.current - pageBufferSize);
        let /** @type {?} */ right = Math.min(this.current + pageBufferSize, pageCount);
        if (this.current - 1 <= pageBufferSize) {
            right = 1 + pageBufferSize * 2;
        }
        if (pageCount - this.current <= pageBufferSize) {
            left = pageCount - pageBufferSize * 2;
        }

        const /** @type {?} */ range: number[] = [];
        for (; left <= right; left++) {
            range.push(left);
        }
        return range;
    }
/**
 * @return {?}
 */
getTotalText(): string {
        const /** @type {?} */ start = (this.current - 1) * this.pageSize + 1;
        const /** @type {?} */ end = Math.min(this.current * this.pageSize, this.total);
        if (this.showTotalFunc) {
            return this.showTotalFunc.apply(this, [this.total, start, end]);
        }

        return `显示${start}-${end}，共${this.total}条数据`;
    }
/**
 * 每页数选择器中的下拉选项格式，默认形如：`10条/页`
 * @param {?} n 每页条数
 * @return {?}
 */
getOptionText(n: number): string {
        return StringUtils.format(this.pageSizeOptionFormat, {
            pageSize: n,
            perPage: this.locale.items_per_page
        });
    }
/**
 * @return {?}
 */
getPaginationClass(): any {
        return {
            [`${this.prefixCls}`]: 1,
            [`${this.prefixCls}-simple`]: this.simple,
            [`mini`]: this.size === 'small',
            [`${this.class}`]: this.class
        };
    }
/**
 * @param {?} event
 * @return {?}
 */
handleInputNum(event: KeyboardEvent): void {
        const /** @type {?} */ keyCode: number = event.keyCode;
        if (!KeyCode.isNumberKey(keyCode) &&
            !EDIT_KEYS.includes(keyCode)
        ) {
            event.preventDefault();
        }
    }

static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'ant-pagination',
    templateUrl: './pagination.html',
    styleUrls: ['./style/index.scss'],
    encapsulation: ViewEncapsulation.None
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer, },
{type: ElementRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'prefixCls': [{ type: Input },],
'total': [{ type: Input },],
'current': [{ type: Input },],
'pageSize': [{ type: Input },],
'showSizeChanger': [{ type: Input },],
'pageSizeOptions': [{ type: Input },],
'pageSizeOptionFormat': [{ type: Input },],
'showQuickJumper': [{ type: Input },],
'size': [{ type: Input },],
'simple': [{ type: Input },],
'showLessItems': [{ type: Input },],
'showTitle': [{ type: Input },],
'showTotal': [{ type: Input },],
'showTotalFunc': [{ type: Input },],
'class': [{ type: Input },],
'locale': [{ type: Input },],
'sizeChange': [{ type: Output },],
'pageChange': [{ type: Output },],
};
}

function Pagination_tsickle_Closure_declarations() {
/** @type {?} */
Pagination.decorators;
/**
 * @nocollapse
 * @type {?}
 */
Pagination.ctorParameters;
/** @type {?} */
Pagination.propDecorators;
/**
 * 样式前缀
 * @type {?}
 */
Pagination.prototype.prefixCls;
/**
 * 数据总数
 * @type {?}
 */
Pagination.prototype.total;
/**
 * 当前页数，默认1
 * @type {?}
 */
Pagination.prototype.current;
/**
 * 每页条数，默认10
 * @type {?}
 */
Pagination.prototype.pageSize;
/**
 * 是否可以改变 pageSize。默认为false
 * @type {?}
 */
Pagination.prototype.showSizeChanger;
/**
 * 指定每页可以显示多少条
 * @type {?}
 */
Pagination.prototype.pageSizeOptions;
/**
 * 每页数选择器中的下拉选项格式，默认形如：`10条/页`
 * @type {?}
 */
Pagination.prototype.pageSizeOptionFormat;
/**
 * 是否可以快速跳转至某页。默认为false
 * @type {?}
 */
Pagination.prototype.showQuickJumper;
/**
 * 当为「small」时，是小尺寸分页
 * @type {?}
 */
Pagination.prototype.size;
/**
 * 当添加该属性时，显示为简单分页
 * @type {?}
 */
Pagination.prototype.simple;
/**
 * 向前后一次跳多少页：showLess为3，否则为5
 * @type {?}
 */
Pagination.prototype.showLessItems;
/**
 * 是否在按钮上显示title
 * @type {?}
 */
Pagination.prototype.showTitle;
/**
 * 是否显示记录总数和当前记录范围
 * @type {?}
 */
Pagination.prototype.showTotal;
/**
 * 自定义显示总数的方法，参数：total-总数，start-开始记录数，end-结束记录数
 * @type {?}
 */
Pagination.prototype.showTotalFunc;
/**
 * 用户CSS样式
 * @type {?}
 */
Pagination.prototype.class;
/**
 * 本地化
 * @type {?}
 */
Pagination.prototype.locale;
/**
 * pageSize变更事件
 * @type {?}
 */
Pagination.prototype.sizeChange;
/**
 * page变更事件
 * @type {?}
 */
Pagination.prototype.pageChange;
/** @type {?} */
Pagination.prototype._current;
/** @type {?} */
Pagination.prototype._pageSize;
/** @type {?} */
Pagination.prototype.renderer;
/** @type {?} */
Pagination.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
